import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientsApi, enterpriseApi, policiesApi } from "@/api/endpoints";
import type { CarrierRecord, CustomerProfile } from "@/api/types";
import { Modal } from "@/components/Modal";
import { PRODUCT_LINES, POLICY_STATUSES } from "@/constants/crm";
import { customerName } from "@/utils/format";

type PresetCustomer = { id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  presetCustomer?: PresetCustomer;
  onCreated?: (policyId: string) => void;
};

const inputStyle = {
  width: "100%",
  padding: "0.6rem 0.75rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
};

const labelWrap = { display: "grid", gap: "0.3rem" } as const;

export function NewPolicyModal({ open, onClose, presetCustomer, onCreated }: Props) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<PresetCustomer | null>(presetCustomer ?? null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<CustomerProfile[]>([]);
  const [carriers, setCarriers] = useState<CarrierRecord[]>([]);
  const [form, setForm] = useState({
    product_line: "Commercial Auto",
    carrier_id: "",
    policy_number: "",
    status: "active",
    effective_date: "",
    expiration_date: "",
    premium: "",
    commission_percent: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setCustomer(presetCustomer ?? null);
  }, [open, presetCustomer]);

  useEffect(() => {
    if (!open) return;
    enterpriseApi.carriers().then((data) => setCarriers(data.items)).catch(() => setCarriers([]));
  }, [open]);

  useEffect(() => {
    if (customer || !search.trim()) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(() => {
      clientsApi
        .list(search.trim())
        .then((data) => active && setResults(data.items.slice(0, 8)))
        .catch(() => active && setResults([]));
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [search, customer]);

  function update(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!customer) {
      setError("Select a client for this policy.");
      return;
    }
    const premiumCents = form.premium.trim() ? Math.round(parseFloat(form.premium) * 100) : undefined;
    if (form.premium.trim() && (premiumCents === undefined || Number.isNaN(premiumCents))) {
      setError("Premium must be a number.");
      return;
    }
    setSaving(true);
    try {
      const created = await policiesApi.create({
        customer_id: customer.id,
        product_line: form.product_line || undefined,
        policy_number: form.policy_number.trim() || undefined,
        carrier_id: form.carrier_id || undefined,
        status: form.status,
        effective_date: form.effective_date || undefined,
        expiration_date: form.expiration_date || undefined,
        premium_cents: premiumCents,
        commission_percent: form.commission_percent.trim()
          ? parseFloat(form.commission_percent)
          : undefined,
      });
      onClose();
      if (onCreated) onCreated(created.id);
      else navigate(`/policies/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create policy.");
      setSaving(false);
    }
  }

  return (
    <Modal open={open} title="New policy" onClose={onClose} width="38rem">
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.85rem" }}>
        <div style={labelWrap}>
          <span className="field-label">Client</span>
          {customer ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <strong>{customer.name}</strong>
              {!presetCustomer ? (
                <button type="button" className="btn btn-secondary" onClick={() => setCustomer(null)}>
                  Change
                </button>
              ) : null}
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <input
                style={inputStyle}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client by name, company, or email"
              />
              {results.length > 0 ? (
                <div
                  className="card"
                  style={{ position: "absolute", zIndex: 5, width: "100%", marginTop: 4, padding: "0.25rem" }}
                >
                  {results.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      className="btn btn-secondary"
                      style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 2 }}
                      onClick={() => {
                        setCustomer({
                          id: client.id,
                          name: customerName(client.first_name, client.last_name, client.company_name),
                        });
                        setSearch("");
                      }}
                    >
                      {customerName(client.first_name, client.last_name, client.company_name)}
                      {client.email ? ` · ${client.email}` : ""}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Product line</span>
            <select style={inputStyle} value={form.product_line} onChange={(e) => update("product_line", e.target.value)}>
              {PRODUCT_LINES.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          </label>
          <label style={labelWrap}>
            <span className="field-label">Carrier (optional)</span>
            <select style={inputStyle} value={form.carrier_id} onChange={(e) => update("carrier_id", e.target.value)}>
              <option value="">—</option>
              {carriers.map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Policy number (blank = auto)</span>
            <input style={inputStyle} value={form.policy_number} onChange={(e) => update("policy_number", e.target.value)} />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Status</span>
            <select style={inputStyle} value={form.status} onChange={(e) => update("status", e.target.value)}>
              {POLICY_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Effective date</span>
            <input style={inputStyle} type="date" value={form.effective_date} onChange={(e) => update("effective_date", e.target.value)} />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Expiration date</span>
            <input style={inputStyle} type="date" value={form.expiration_date} onChange={(e) => update("expiration_date", e.target.value)} />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label style={labelWrap}>
            <span className="field-label">Annual premium ($)</span>
            <input style={inputStyle} inputMode="decimal" value={form.premium} onChange={(e) => update("premium", e.target.value)} placeholder="18750.00" />
          </label>
          <label style={labelWrap}>
            <span className="field-label">Commission %</span>
            <input style={inputStyle} inputMode="decimal" value={form.commission_percent} onChange={(e) => update("commission_percent", e.target.value)} placeholder="12.5" />
          </label>
        </div>

        {error ? <div className="error-banner">{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create policy"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
